import { CircularProgress } from '@material-ui/core';
import Chip from '@mui/material/Chip';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import FormatService from 'services/formatService';
import { useGetActivityFeedLogByWorkOrderIdQuery } from 'features/activity/activitySlice';
import { useGetAllIssuesTrackerQuery } from 'features/IssuesTracker/issuesTrackerSlice';
import { useTicketDialog } from 'features/mechanicalWork/useTicketDialog';
import { selectIsVendor } from 'features/user/userSlice';
import { activityTimelineStyles } from './ActivityTimelineStyles';
import { activityTimelineMessages, creationFeedLogTypes, entryIconByType, ticketFeedLogTypes, vendorTagLabel, VENDOR_FEED_SOURCE, WO_LEVEL_LOGS_LAUNCH_DATE } from './ActivityTimelineConsts';

const ActivityTimeline = ({ workOrderId, vehicleId, workOrderCreatedDate, sticky = true, onOpenTicket }) => {
    const classes = activityTimelineStyles();
    const { openTicket, prefetch, ticketDialog } = useTicketDialog(onOpenTicket);
    const isVendor = useSelector(selectIsVendor);

    const { data: entries = [], isLoading, isError, error } = useGetActivityFeedLogByWorkOrderIdQuery(workOrderId, { skip: !workOrderId });
    const { data: tickets } = useGetAllIssuesTrackerQuery({ includeIssueTrackerHistory: false, vehicleId }, { skip: !vehicleId || isVendor });

    const linkedTicketId = useMemo(
        () => tickets?.find(t => t?.linkedWorkOrders?.some(wo => wo?.id === workOrderId))?.id,
        [tickets, workOrderId]
    );

    const sortedEntries = useMemo(
        () => [...entries].sort((a, b) => {
            const aTime = moment(a.createdDate).valueOf();
            const bTime = moment(b.createdDate).valueOf();
            const aIsCreation = creationFeedLogTypes.includes(a.type);
            const bIsCreation = creationFeedLogTypes.includes(b.type);
            if (aIsCreation !== bIsCreation && Math.abs(aTime - bTime) <= 60000) {
                return aIsCreation ? -1 : 1;
            }
            return aTime - bTime;
        }),
        [entries]
    );

    const renderBody = () => {
        if (isLoading) {
            return <CircularProgress size={20} />;
        }
        if (isError) {
            return error?.status === 404
                ? <div className={classes.stateMessage}>{activityTimelineMessages.notAvailable}</div>
                : <div className={classes.errorMessage}>{activityTimelineMessages.error}</div>;
        }
        if (sortedEntries.length === 0) {
            const isLegacy = workOrderCreatedDate && moment(workOrderCreatedDate).isBefore(moment(WO_LEVEL_LOGS_LAUNCH_DATE));
            return <div className={classes.stateMessage}>{isLegacy ? activityTimelineMessages.legacy : activityTimelineMessages.empty}</div>;
        }
        return (
            <ul className={classes.list}>
                {sortedEntries.map(entry => {
                    const isTicket = ticketFeedLogTypes.includes(entry.type);
                    const clickable = isTicket && !!linkedTicketId && !isVendor;
                    const EntryIcon = entryIconByType[entry.type];
                    return (
                        <li
                            key={entry.id}
                            className={`${classes.entry} ${clickable ? classes.ticketEntry : ''}`}
                            onMouseEnter={clickable ? () => prefetch(linkedTicketId) : undefined}
                            onClick={clickable ? () => openTicket(linkedTicketId) : undefined}
                        >
                            <span className={classes.marker}>
                                {EntryIcon
                                    ? <EntryIcon fontSize="small" />
                                    : <FiberManualRecordIcon className={classes.fallbackIcon} />}
                            </span>
                            <div className={classes.body}>
                                <div className={classes.description}>{entry.description}</div>
                                <div className={classes.meta}>
                                    <span>{entry.createdBy || 'System'}</span>
                                    {entry.source === VENDOR_FEED_SOURCE &&
                                        <Chip label={vendorTagLabel} size="small" className={classes.vendorChip} />}
                                    {entry.createdDate &&
                                        <>
                                            <span className={classes.metaSeparator}>·</span>
                                            <span>{FormatService.formatDateTime(entry.createdDate)}</span>
                                        </>}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <div className={sticky ? classes.panel : undefined}>
            <div className={classes.title}>Activity Timeline</div>
            {renderBody()}
            {ticketDialog}
        </div>
    );
};

export default ActivityTimeline;
